import { For, Show, createSignal, onMount } from "solid-js";
import { personName, type ClassTeam, type ClassTeamModule } from "./classTeam";
import type { TeacherClass } from "./classes";

// Team einer Klasse: wer bearbeitet, wer liest mit, offene Übernahme-Anfragen.
// Mitlesende tragen nur Schulleitungen ein (Schulübersicht); hier wird übergeben, angefragt und entschieden.
export function ClassTeamModal(props: { value: TeacherClass; module: ClassTeamModule; onClose: () => void; onChanged: () => Promise<unknown> }) {
  const [team, setTeam] = createSignal<ClassTeam>();
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [info, setInfo] = createSignal("");
  const canEdit = () => props.value.access_level !== "read";

  const load = async () => {
    try { setTeam(await props.module.get(props.value.id)); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Das Team konnte nicht geladen werden."); }
  };
  onMount(() => { void load(); });

  const run = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await work();
      setInfo(done);
      await Promise.all([load(), props.onChanged()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Das hat nicht geklappt.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="modal-overlay active" style={{ "z-index": 9000 }} onClick={(event) => event.target === event.currentTarget && props.onClose()}>
      <div style={{ background: "#fff", "border-radius": "16px", padding: "28px", "max-width": "480px", width: "100%", "box-shadow": "0 20px 60px rgba(0,0,0,0.3)" }} role="dialog" aria-modal="true" aria-label="Lehrkräfte-Team">
        <h2 style={{ "margin-bottom": "4px" }}>Lehrkräfte der Klasse {props.value.name}</h2>
        <p style={{ color: "var(--text-muted)", "font-size": "0.88rem", "margin-bottom": "16px" }}>
          Alle Lehrkräfte dieser Klasse sehen alle Einträge, auch den Kummerkasten. Bearbeiten kann immer nur eine Lehrkraft.
        </p>
        <Show when={team()} fallback={<Show when={!error()}><div style={{ display: "flex", "justify-content": "center", padding: "20px" }}><div class="loading-spinner" /></div></Show>}>
          {(data) => (
            <>
              <div class="sr-legend">Bearbeitet von</div>
              <p style={{ "margin-bottom": "12px" }}><strong>{personName(data().editor)}</strong></p>

              <div class="sr-legend">Liest mit</div>
              <Show when={data().readers.length > 0} fallback={<p class="sr-hint">Niemand. Die Schulleitung kann Lehrkräfte als Mitlesende eintragen.</p>}>
                <ul style={{ "list-style": "none", padding: 0, margin: "0 0 12px" }}>
                  <For each={data().readers}>{(reader) => (
                    <li style={{ display: "flex", "justify-content": "space-between", "align-items": "center", gap: "8px", padding: "4px 0" }}>
                      <span>{personName(reader)}</span>
                      <Show when={canEdit()}>
                        <button type="button" class="sr-linkbtn" disabled={busy()} onClick={() => void run(() => props.module.handOver(props.value.id, reader.id), `Die Bearbeitung liegt jetzt bei ${personName(reader)}.`)}>
                          Bearbeitung übergeben
                        </button>
                      </Show>
                    </li>
                  )}</For>
                </ul>
              </Show>

              <Show when={data().requests.length > 0}>
                <div class="sr-legend">Übernahme angefragt</div>
                <ul style={{ "list-style": "none", padding: 0, margin: "0 0 12px" }}>
                  <For each={data().requests}>{(request) => (
                    <li style={{ display: "flex", "justify-content": "space-between", "align-items": "center", gap: "8px", padding: "4px 0" }}>
                      <span>{personName(request)}</span>
                      <Show when={canEdit()} fallback={<span class="so-pill wait">wartet</span>}>
                        <span style={{ display: "flex", gap: "10px" }}>
                          <button type="button" class="sr-linkbtn" disabled={busy()} onClick={() => void run(() => props.module.decide(props.value.id, request.id, true), "Die Bearbeitung wurde übergeben.")}>Annehmen</button>
                          <button type="button" class="sr-linkbtn" disabled={busy()} onClick={() => void run(() => props.module.decide(props.value.id, request.id, false), "Die Anfrage wurde abgelehnt.")}>Ablehnen</button>
                        </span>
                      </Show>
                    </li>
                  )}</For>
                </ul>
              </Show>
            </>
          )}
        </Show>
        <Show when={info()}><p class="so-info" role="status">{info()}</p></Show>
        <Show when={error()}><div class="form-error" style={{ margin: "10px 0" }} role="alert">{error()}</div></Show>
        <div style={{ display: "flex", gap: "12px", "margin-top": "16px" }}>
          <Show when={!canEdit() && team() && !team()!.myRequestPending}>
            <button class="btn btn-primary btn-full" type="button" disabled={busy()} onClick={() => void run(() => props.module.requestTakeover(props.value.id), "Die Anfrage wurde gesendet.")}>
              Bearbeitung anfragen
            </button>
          </Show>
          <button class="btn btn-secondary btn-full" type="button" onClick={props.onClose}>Schließen</button>
        </div>
      </div>
    </div>
  );
}
